/**
 * Chart SVG builder shared by the viewer (React, via innerHTML) and the PDF
 * renderer. Pure string output, brand colour in, no dependencies. Sizes use
 * a 1000×560 viewBox so cqw-based containers scale it.
 */
export interface ChartSpec {
  type?: 'bar' | 'column' | 'line' | 'pie' | 'donut';
  labels?: string[];
  datasets?: Array<{ label?: string; values?: number[]; /** 'percent' → values are already ×100 and get a % sign. */ format?: string }>;
}

export interface ChartColors {
  primary: string;
  accent: string;
  text: string;
  textMuted: string;
  grid: string;
  /** Extra series / pie slices. */
  palette?: string[];
}

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

/** Values are shares when the dataset says so — show them with a % sign. */
function isPct(d?: { label?: string; format?: string }): boolean {
  return d?.format === 'percent' || /share|percent|pct|%/i.test(String(d?.label || ''));
}
function valueFmt(v: number, d?: { label?: string; format?: string }): string {
  return isPct(d) ? `${fmt(v)}%` : fmt(v);
}

function fmt(v: number): string {
  if (!Number.isFinite(v)) return '';
  if (Math.abs(v) >= 1_000_000) return `${Math.round(v / 100_000) / 10}M`;
  if (Math.abs(v) >= 10_000) return `${Math.round(v / 1000)}k`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 100) / 10}k`;
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 10) / 10);
}

const W = 1000, H = 560;
const FONT = 'font-family="inherit"';

export function chartSvg(chart: ChartSpec, c: ChartColors): string {
  const labels = chart.labels ?? [];
  const series = (chart.datasets ?? []).filter((d) => Array.isArray(d.values));
  const palette = c.palette && c.palette.length ? c.palette : [c.primary, c.accent, '#F59E0B', '#10B981', '#EC4899', '#6366F1'];
  const type = chart.type ?? 'bar';
  if (!labels.length || !series.length) {
    return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg"><text x="${W / 2}" y="${H / 2}" text-anchor="middle" fill="${c.textMuted}" font-size="28" ${FONT}>No data yet</text></svg>`;
  }
  if (type === 'pie' || type === 'donut') return pieSvg(labels, series[0].values ?? [], palette, c, type === 'donut');
  if (type === 'line') return lineSvg(labels, series, palette, c);
  if (type === 'column') return columnSvg(labels, series, palette, c);
  return barSvg(labels, series, palette, c);
}

function barSvg(labels: string[], series: ChartSpec['datasets'] & object, palette: string[], c: ChartColors): string {
  const s = series as Array<{ label?: string; values?: number[]; format?: string }>;
  const max = Math.max(1, ...s.flatMap((d) => d.values ?? []));
  const rows = labels.length;
  const rowH = Math.min(70, (H - 40) / rows);
  // Label column grows with the longest category (capped) and long names get an ellipsis + tooltip instead of being clipped.
  const fs = rows > 10 ? 16 : 20;
  const cw = fs * 0.52;
  const longest = Math.max(1, ...labels.map((l) => String(l ?? '').length));
  const labelW = Math.min(340, Math.max(180, Math.ceil(longest * cw) + 30));
  const maxChars = Math.max(6, Math.floor((labelW - 30) / cw));
  const barsPerRow = s.length;
  const barH = Math.max(8, (rowH - 16) / barsPerRow);
  let out = '';
  labels.forEach((label, i) => {
    const y = 20 + i * rowH;
    const full = String(label ?? '');
    const shown = full.length > maxChars ? `${full.slice(0, maxChars - 1)}…` : full;
    out += `<text x="${labelW - 16}" y="${y + rowH / 2 + (fs > 16 ? 6 : 5)}" text-anchor="end" fill="${c.textMuted}" font-size="${fs}" font-weight="500" ${FONT}><title>${esc(full)}</title>${esc(shown)}</text>`;
    s.forEach((d, si) => {
      const v = d.values?.[i] ?? 0;
      const w = Math.max(6, (v / max) * (W - labelW - 110));
      const by = y + 8 + si * barH;
      out += `<rect x="${labelW}" y="${by}" width="${w}" height="${barH - 4}" rx="6" fill="${palette[si % palette.length]}"/>`;
      out += `<text x="${labelW + w + 12}" y="${by + (barH - 4) / 2 + 7}" fill="${c.text}" font-size="20" font-weight="700" ${FONT}>${esc(valueFmt(v, s[si]))}</text>`;
    });
  });
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}${legend(s, palette, c)}</svg>`;
}

function columnSvg(labels: string[], series: ChartSpec['datasets'] & object, palette: string[], c: ChartColors): string {
  const s = series as Array<{ label?: string; values?: number[]; format?: string }>;
  const axisSfx = s.every((d) => isPct(d)) ? '%' : '';
  const max = Math.max(1, ...s.flatMap((d) => d.values ?? []));
  const left = 70, bottom = H - 70, top = 40;
  const groupW = (W - left - 30) / labels.length;
  const colW = Math.max(10, (groupW * 0.7) / s.length);
  let out = '';
  for (let g = 0; g <= 4; g++) {
    const y = top + ((bottom - top) * g) / 4;
    out += `<line x1="${left}" x2="${W - 30}" y1="${y}" y2="${y}" stroke="${c.grid}" stroke-width="1"/>`;
    out += `<text x="${left - 10}" y="${y + 6}" text-anchor="end" fill="${c.textMuted}" font-size="16" ${FONT}>${esc(fmt(max - (max * g) / 4))}${axisSfx}</text>`;
  }
  labels.forEach((label, i) => {
    const gx = left + i * groupW + (groupW - colW * s.length) / 2;
    s.forEach((d, si) => {
      const v = d.values?.[i] ?? 0;
      const h = Math.max(4, (v / max) * (bottom - top));
      const x = gx + si * colW;
      out += `<rect x="${x}" y="${bottom - h}" width="${colW - 4}" height="${h}" rx="6" fill="${palette[si % palette.length]}"/>`;
      out += `<text x="${x + (colW - 4) / 2}" y="${bottom - h - 8}" text-anchor="middle" fill="${c.text}" font-size="16" font-weight="700" ${FONT}>${esc(valueFmt(v, s[si]))}</text>`;
    });
    // Long handles / many columns: smaller type and an ellipsis so labels never collide.
    const maxChars = Math.max(6, Math.floor(groupW / (labels.length >= 6 ? 8.5 : 10)));
    const shown = label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label;
    out += `<text x="${left + i * groupW + groupW / 2}" y="${bottom + 28}" text-anchor="middle" fill="${c.textMuted}" font-size="${labels.length >= 6 || label.length > 12 ? 14 : 18}" font-weight="500" ${FONT}><title>${esc(label)}</title>${esc(shown)}</text>`;
  });
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}${legend(s, palette, c)}</svg>`;
}

function lineSvg(labels: string[], series: ChartSpec['datasets'] & object, palette: string[], c: ChartColors): string {
  const s = series as Array<{ label?: string; values?: number[]; format?: string }>;
  const axisSfx = s.every((d) => isPct(d)) ? '%' : '';
  const max = Math.max(1, ...s.flatMap((d) => d.values ?? []));
  const left = 70, right = W - 30, bottom = H - 70, top = 40;
  const stepX = labels.length > 1 ? (right - left) / (labels.length - 1) : 0;
  let out = '';
  for (let g = 0; g <= 4; g++) {
    const y = top + ((bottom - top) * g) / 4;
    out += `<line x1="${left}" x2="${right}" y1="${y}" y2="${y}" stroke="${c.grid}" stroke-width="1"/>`;
    out += `<text x="${left - 10}" y="${y + 6}" text-anchor="end" fill="${c.textMuted}" font-size="16" ${FONT}>${esc(fmt(max - (max * g) / 4))}${axisSfx}</text>`;
  }
  labels.forEach((label, i) => {
    out += `<text x="${left + i * stepX}" y="${bottom + 28}" text-anchor="middle" fill="${c.textMuted}" font-size="18" font-weight="500" ${FONT}>${esc(label)}</text>`;
  });
  s.forEach((d, si) => {
    const pts = labels.map((_, i) => `${left + i * stepX},${bottom - ((d.values?.[i] ?? 0) / max) * (bottom - top)}`);
    const color = palette[si % palette.length];
    out += `<polyline points="${pts.join(' ')}" fill="none" stroke="${color}" stroke-width="5" stroke-linejoin="round" stroke-linecap="round"/>`;
    labels.forEach((_, i) => {
      const [x, y] = pts[i].split(',').map(Number);
      out += `<circle cx="${x}" cy="${y}" r="7" fill="#fff" stroke="${color}" stroke-width="4"/>`;
      if (si === 0) out += `<text x="${x}" y="${y - 14}" text-anchor="middle" fill="${c.text}" font-size="16" font-weight="700" ${FONT}>${esc(fmt(d.values?.[i] ?? 0))}</text>`;
    });
  });
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}${legend(s, palette, c)}</svg>`;
}

function pieSvg(labels: string[], values: number[], palette: string[], c: ChartColors, donut: boolean): string {
  const total = values.reduce((a, b) => a + (b || 0), 0) || 1;
  const cx = 300, cy = H / 2, r = 220, ri = donut ? 130 : 0;
  let angle = -Math.PI / 2;
  let out = '';
  labels.forEach((label, i) => {
    const v = values[i] || 0;
    const a = (v / total) * Math.PI * 2;
    const x1 = cx + r * Math.cos(angle), y1 = cy + r * Math.sin(angle);
    const x2 = cx + r * Math.cos(angle + a), y2 = cy + r * Math.sin(angle + a);
    const large = a > Math.PI ? 1 : 0;
    const color = palette[i % palette.length];
    if (v > 0) {
      if (donut) {
        const xi1 = cx + ri * Math.cos(angle + a), yi1 = cy + ri * Math.sin(angle + a);
        const xi2 = cx + ri * Math.cos(angle), yi2 = cy + ri * Math.sin(angle);
        out += `<path d="M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${xi1},${yi1} A${ri},${ri} 0 ${large} 0 ${xi2},${yi2} Z" fill="${color}" stroke="#fff" stroke-width="3"/>`;
      } else {
        out += `<path d="M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z" fill="${color}" stroke="#fff" stroke-width="3"/>`;
      }
    }
    angle += a;
    const ly = 90 + i * 52;
    out += `<rect x="600" y="${ly - 16}" width="22" height="22" rx="6" fill="${color}"/>`;
    out += `<text x="636" y="${ly + 2}" fill="${c.text}" font-size="22" font-weight="600" ${FONT}>${esc(label)}</text>`;
    out += `<text x="${W - 40}" y="${ly + 2}" text-anchor="end" fill="${c.textMuted}" font-size="22" ${FONT}>${esc(fmt(v))} · ${Math.round((v / total) * 100)}%</text>`;
  });
  if (donut) out += `<text x="${cx}" y="${cy + 12}" text-anchor="middle" fill="${c.text}" font-size="40" font-weight="800" ${FONT}>${esc(fmt(total))}</text>`;
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}</svg>`;
}

function legend(s: Array<{ label?: string }>, palette: string[], c: ChartColors): string {
  if (s.length < 2) return '';
  let x = 70;
  let out = '';
  s.forEach((d, i) => {
    out += `<rect x="${x}" y="${H - 26}" width="18" height="18" rx="5" fill="${palette[i % palette.length]}"/>`;
    out += `<text x="${x + 26}" y="${H - 11}" fill="${c.textMuted}" font-size="18" ${FONT}>${esc(d.label || `Series ${i + 1}`)}</text>`;
    x += 60 + (d.label || `Series ${i + 1}`).length * 10;
  });
  return out;
}
